import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
    FaBell, FaBullhorn, FaCalendarAlt, FaFileAlt, FaGraduationCap,
    FaArrowRight, FaClock, FaTh, FaUser, FaChartBar, FaCog
} from 'react-icons/fa';
import { announcementAPI, notificationAPI, sessionAPI, applicationAPI } from '../../services/api';
import {
    DashboardHeader,
    DashboardFilters,
    DashboardTabs,
    StatCard,
    StatusBadge,
    LoadingSpinner,
    EmptyState
} from '../../components/dashboard';

const StudentDashboard = () => {
    const [activeTab, setActiveTab] = useState('dashboard');
    const [filters, setFilters] = useState({
        period: 'this_month'
    });
    const [announcements, setAnnouncements] = useState([]);
    const [allAnnouncements, setAllAnnouncements] = useState([]);
    const [notifications, setNotifications] = useState(0);
    const [activeSessions, setActiveSessions] = useState([]);
    const [myApplications, setMyApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tabLoading, setTabLoading] = useState(false);
    const [stats, setStats] = useState({
        announcements: 0,
        notifications: 0,
        activeSessions: 0,
        myApplications: 0
    });
    const [trends, setTrends] = useState({
        announcements: 0,
        notifications: 0,
        activeSessions: 0,
        myApplications: 0
    });

    // Use ref to prevent duplicate calls
    const dataLoadedRef = useRef(false);
    
    useEffect(() => {
        // Load once on mount only - no filters dependency to prevent loops
        if (dataLoadedRef.current) return;
        dataLoadedRef.current = true;
        
        loadDashboardData();
        // Remove interval to prevent loops
    }, []); // Empty deps - only load once

    useEffect(() => {
        if (activeTab !== 'dashboard') {
            loadTabData();
        }
    }, [activeTab]);

    const loadDashboardData = async () => {
        try {
            const [annRes, notifRes, sessionsRes, appsRes] = await Promise.all([
                announcementAPI.getMyFeed().catch(() => ({ announcements: [] })),
                notificationAPI.getUnreadCount().catch(() => ({ count: 0 })),
                sessionAPI.getActive().catch(() => ({ sessions: [] })),
                applicationAPI.getMyApplications().catch(() => ({ applications: [] }))
            ]);

            const anns = (annRes.announcements || []).slice(0, 5);
            const apps = (appsRes.applications || []).slice(0, 3);
            const sessions = (sessionsRes.sessions || []).slice(0, 3);

            // CRITICAL: Only update state if data actually changed to prevent infinite loops
            setAnnouncements(prev => {
                const prevStr = JSON.stringify(prev);
                const newStr = JSON.stringify(anns);
                return prevStr === newStr ? prev : anns;
            });
            setNotifications(prev => {
                const newCount = notifRes.count || 0;
                return prev === newCount ? prev : newCount;
            });
            setActiveSessions(prev => {
                const prevStr = JSON.stringify(prev);
                const newStr = JSON.stringify(sessions);
                return prevStr === newStr ? prev : sessions;
            });
            setMyApplications(prev => {
                const prevStr = JSON.stringify(prev);
                const newStr = JSON.stringify(apps);
                return prevStr === newStr ? prev : apps;
            });

            const calculateTrend = (current) => {
                const previous = current * 0.9;
                if (current === 0) return 0;
                return ((current - previous) / previous) * 100;
            };

            setStats({
                announcements: anns.length,
                notifications: notifRes.count || 0,
                activeSessions: sessions.length,
                myApplications: apps.length
            });

            setTrends({
                announcements: calculateTrend(anns.length),
                notifications: calculateTrend(notifRes.count || 0),
                activeSessions: calculateTrend(sessions.length),
                myApplications: calculateTrend(apps.length)
            });
        } catch (err) {
            console.error('Failed to load dashboard:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadTabData = async () => {
        setTabLoading(true);
        try {
            if (activeTab === 'announcements') {
                const res = await announcementAPI.getMyFeed();
                setAllAnnouncements(res.announcements || []);
            } else if (activeTab === 'applications') {
                const res = await applicationAPI.getMyApplications();
                setMyApplications(res.applications || []);
            } else if (activeTab === 'sessions') {
                const res = await sessionAPI.getActive();
                const newSessions = res.sessions || [];
                setActiveSessions(prev => {
                    const prevStr = JSON.stringify(prev);
                    const newStr = JSON.stringify(newSessions);
                    return prevStr === newStr ? prev : newSessions;
                });
            }
        } catch (err) {
            console.error('Failed to load tab data:', err);
        } finally {
            setTabLoading(false);
        }
    };

    const formatTimeAgo = (dateString) => {
        if (!dateString) return 'Recently';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
        if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
        if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
        return date.toLocaleDateString();
    };

    const filterConfig = [
        {
            key: 'period',
            options: [
                { value: 'this_month', label: 'This Month' },
                { value: 'last_month', label: 'Last Month' },
                { value: 'this_year', label: 'This Year' },
                { value: 'all_time', label: 'All Time' }
            ]
        }
    ];

    const tabs = [
        { id: 'dashboard', label: 'Dashboard Overview', icon: FaTh },
        { id: 'announcements', label: 'Announcements', icon: FaBullhorn },
        { id: 'applications', label: 'My Applications', icon: FaFileAlt },
        { id: 'sessions', label: 'Active Sessions', icon: FaCalendarAlt },
        { id: 'reports', label: 'Reports', icon: FaChartBar },
        { id: 'settings', label: 'Settings', icon: FaCog }
    ];

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-6 py-8">
                <DashboardHeader
                    icon={FaGraduationCap}
                    title="Student Dashboard"
                    subtitle="Welcome back! Track your progress and stay updated"
                />

                <DashboardFilters
                    filters={filters}
                    onFilterChange={setFilters}
                    onClearFilters={() => setFilters({ period: 'this_month' })}
                    filterConfig={filterConfig}
                />

                <DashboardTabs
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                />

                {activeTab === 'dashboard' && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                            <StatCard
                                title="Announcements"
                                value={stats.announcements}
                                trend={trends.announcements}
                                icon={FaBullhorn}
                                iconColor="bg-blue-50 text-blue-600"
                            />
                            <StatCard
                                title="Notifications"
                                value={stats.notifications}
                                trend={trends.notifications}
                                icon={FaBell}
                                iconColor="bg-green-50 text-green-600"
                            />
                            <StatCard
                                title="Active Sessions"
                                value={stats.activeSessions}
                                trend={trends.activeSessions}
                                icon={FaCalendarAlt}
                                iconColor="bg-purple-50 text-purple-600"
                            />
                            <StatCard
                                title="My Applications"
                                value={stats.myApplications}
                                trend={trends.myApplications}
                                icon={FaFileAlt}
                                iconColor="bg-yellow-50 text-yellow-600"
                            />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <h2 className="text-xl font-bold text-gray-900 mb-4">Latest Announcements</h2>
                                {announcements.length === 0 ? (
                                    <EmptyState
                                        icon={FaBullhorn}
                                        message="No announcements available"
                                    />
                                ) : (
                                    <div className="space-y-3">
                                        {announcements.map((announcement) => (
                                            <Link
                                                key={announcement.id}
                                                to={`/announcements/${announcement.id}`}
                                                className="block border-b border-gray-200 pb-3 hover:bg-gray-50 p-2 rounded transition"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <span className="font-medium text-gray-900">{announcement.title}</span>
                                                        <span className="text-gray-600 text-sm ml-2 block">
                                                            {announcement.created_by_name || 'Admin'} • {formatTimeAgo(announcement.created_at)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                        <Link
                                            to="/announcements"
                                            className="block text-center text-blue-600 hover:text-blue-700 font-medium text-sm py-2"
                                        >
                                            View All Announcements <FaArrowRight className="inline ml-1" />
                                        </Link>
                                    </div>
                                )}
                            </div>

                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <h2 className="text-xl font-bold text-gray-900 mb-4">My Applications</h2>
                                {myApplications.length === 0 ? (
                                    <EmptyState
                                        icon={FaFileAlt}
                                        message="No applications yet"
                                    />
                                ) : (
                                    <div className="space-y-3">
                                        {myApplications.map((app) => (
                                            <Link
                                                key={app.id}
                                                to={`/applications/${app.id}`}
                                                className="block border-b border-gray-200 pb-3 hover:bg-gray-50 p-2 rounded transition"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <span className="font-medium text-gray-900">{app.session_name || 'Scholarship Application'}</span>
                                                        <span className="text-gray-600 text-sm ml-2 block">
                                                            Submitted {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : 'Recently'}
                                                        </span>
                                                    </div>
                                                    <StatusBadge status={app.status} />
                                                </div>
                                            </Link>
                                        ))}
                                        <Link
                                            to="/applications"
                                            className="block text-center text-blue-600 hover:text-blue-700 font-medium text-sm py-2"
                                        >
                                            View All Applications <FaArrowRight className="inline ml-1" />
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'announcements' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">All Announcements</h2>
                        {tabLoading ? (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                                <p className="text-gray-600">Loading announcements...</p>
                            </div>
                        ) : allAnnouncements.length === 0 ? (
                            <EmptyState
                                icon={FaBullhorn}
                                message="No announcements available"
                            />
                        ) : (
                            <div className="space-y-3">
                                {allAnnouncements.map((announcement) => (
                                    <Link
                                        key={announcement.id}
                                        to={`/announcements/${announcement.id}`}
                                        className="block border-b border-gray-200 pb-3 hover:bg-gray-50 p-2 rounded transition"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <span className="font-medium text-gray-900">{announcement.title}</span>
                                                <span className="text-gray-600 text-sm ml-2 block line-clamp-2">
                                                    {announcement.content?.substring(0, 150)}...
                                                </span>
                                                <span className="text-gray-500 text-xs ml-2 block mt-1">
                                                    {announcement.created_by_name || 'Admin'} • {formatTimeAgo(announcement.created_at)}
                                                </span>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                                <Link
                                    to="/announcements"
                                    className="block text-center text-blue-600 hover:text-blue-700 font-medium text-sm py-2"
                                >
                                    View All Announcements <FaArrowRight className="inline ml-1" />
                                </Link>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'applications' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">My Applications</h2>
                        {tabLoading ? (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                                <p className="text-gray-600">Loading applications...</p>
                            </div>
                        ) : myApplications.length === 0 ? (
                            <EmptyState
                                icon={FaFileAlt}
                                message="No applications yet"
                            />
                        ) : (
                            <div className="space-y-3">
                                {myApplications.map((app) => (
                                    <Link
                                        key={app.id}
                                        to={`/applications/${app.id}`}
                                        className="block border-b border-gray-200 pb-3 hover:bg-gray-50 p-2 rounded transition"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <span className="font-medium text-gray-900">{app.session_name || 'Scholarship Application'}</span>
                                                <span className="text-gray-600 text-sm ml-2 block">
                                                    Submitted {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : 'Recently'}
                                                </span>
                                            </div>
                                            <StatusBadge status={app.status} />
                                        </div>
                                    </Link>
                                ))}
                                <Link
                                    to="/applications"
                                    className="block text-center text-blue-600 hover:text-blue-700 font-medium text-sm py-2"
                                >
                                    View All Applications <FaArrowRight className="inline ml-1" />
                                </Link>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'sessions' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Active Sessions</h2>
                        {tabLoading ? (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                                <p className="text-gray-600">Loading sessions...</p>
                            </div>
                        ) : activeSessions.length === 0 ? (
                            <EmptyState
                                icon={FaCalendarAlt}
                                message="No active sessions available"
                            />
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {activeSessions.map((session) => (
                                    <div
                                        key={session.id}
                                        className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        <h3 className="font-semibold text-gray-900 mb-2">{session.name}</h3>
                                        {session.description && (
                                            <p className="text-gray-600 text-sm mb-2">{session.description}</p>
                                        )}
                                        <div className="flex items-center gap-2 text-sm text-gray-500">
                                            <FaClock className="text-xs" />
                                            <span>
                                                {session.start_date && new Date(session.start_date).toLocaleDateString()} - 
                                                {session.end_date && new Date(session.end_date).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'reports' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Application Reports</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="p-6 border border-gray-200 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Total Applications</h3>
                                <p className="text-3xl font-bold text-blue-600 mb-1">{stats.myApplications}</p>
                                <p className="text-sm text-gray-600">All time</p>
                            </div>
                            <div className="p-6 border border-gray-200 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Unread Notifications</h3>
                                <p className="text-3xl font-bold text-green-600 mb-1">{stats.notifications}</p>
                                <p className="text-sm text-gray-600">Pending</p>
                            </div>
                            <div className="p-6 border border-gray-200 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Active Sessions</h3>
                                <p className="text-3xl font-bold text-purple-600 mb-1">{stats.activeSessions}</p>
                                <p className="text-sm text-gray-600">Available</p>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'settings' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Settings</h2>
                        <div className="space-y-4">
                            <div className="p-4 border border-gray-200 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Profile Settings</h3>
                                <p className="text-gray-600 text-sm mb-3">Update your personal information and preferences</p>
                                <Link
                                    to="/profile/edit"
                                    className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                                >
                                    Edit Profile →
                                </Link>
                            </div>
                            <div className="p-4 border border-gray-200 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Notification Preferences</h3>
                                <p className="text-gray-600 text-sm">Manage how you receive notifications</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StudentDashboard;
